import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { RoutesEnum } from '../lib/routes'
import { useLang } from '../lib/useLang'

const STORAGE_KEY = 'cookie_consent'
const CONSENT_VERSION = 1

/**
 * Возвращает сохранённый выбор пользователя по cookie, либо null, если выбор не сделан.
 * Используйте перед инициализацией аналитики/рекламных скриптов.
 * @returns {{ necessary: boolean, analytics: boolean, marketing: boolean, v: number, ts: number } | null}
 */
export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed?.v !== CONSENT_VERSION) return null
    return parsed
  } catch {
    return null
  }
}

/**
 * Баннер согласия на использование файлов cookie (152-ФЗ / 420-ФЗ).
 * Показывается при первом визите, позволяет принять все, оставить только
 * необходимые или настроить категории. Выбор хранится в localStorage.
 * Необходимые cookie всегда включены; аналитические/маркетинговые — по согласию.
 */
export default function CookieConsent() {
  const { t } = useLang()
  const [visible, setVisible] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [analytics, setAnalytics] = useState(false)
  const [marketing, setMarketing] = useState(false)

  useEffect(() => {
    if (!getCookieConsent()) setVisible(true)
  }, [])

  const persist = (choice) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...choice, necessary: true, v: CONSENT_VERSION, ts: Date.now() })
      )
    } catch {
      /* storage unavailable — banner just won't persist */
    }
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div
      className='fixed inset-x-0 bottom-0 z-[100] px-4 pb-4 sm:px-6 sm:pb-6'
      role='dialog'
      aria-live='polite'
      aria-label={t('cookieBanner.title')}
    >
      <div className='mx-auto max-w-3xl rounded-2xl border border-ink/10 bg-cream text-ink shadow-2xl'>
        <div className='p-5 sm:p-6'>
          <div className='text-[0.95rem] font-semibold mb-2'>{t('cookieBanner.title')}</div>
          <p className='text-[0.9rem] leading-[1.6] text-ink/70'>
            {t('cookieBanner.text')}{' '}
            <Link to={RoutesEnum.COOKIES} className='underline hover:text-ink'>
              {t('cookieBanner.policyLink')}
            </Link>
          </p>

          {showSettings && (
            <div className='mt-4 space-y-3'>
              <CategoryRow
                title={t('cookieBanner.catNecessaryTitle')}
                desc={t('cookieBanner.catNecessaryDesc')}
                checked
                disabled
                stateLabel={t('cookieBanner.catNecessaryState')}
              />
              <CategoryRow
                title={t('cookieBanner.catAnalyticsTitle')}
                desc={t('cookieBanner.catAnalyticsDesc')}
                checked={analytics}
                onChange={() => setAnalytics((v) => !v)}
              />
              <CategoryRow
                title={t('cookieBanner.catMarketingTitle')}
                desc={t('cookieBanner.catMarketingDesc')}
                checked={marketing}
                onChange={() => setMarketing((v) => !v)}
              />
            </div>
          )}

          <div className='mt-5 flex flex-col md:flex-row gap-3'>
            {!showSettings ? (
              <>
                <button
                  type='button'
                  className='header-button-primary flex-1'
                  onClick={() => persist({ analytics: true, marketing: true })}
                >
                  {t('cookieBanner.acceptAll')}
                </button>
                <button
                  type='button'
                  className='header-button-secondary flex-1'
                  onClick={() => persist({ analytics: false, marketing: false })}
                >
                  {t('cookieBanner.necessaryOnly')}
                </button>
                <button
                  type='button'
                  className='header-button-secondary flex-1'
                  onClick={() => setShowSettings(true)}
                >
                  {t('cookieBanner.settings')}
                </button>
              </>
            ) : (
              <button
                type='button'
                className='header-button-primary flex-1'
                onClick={() => persist({ analytics, marketing })}
              >
                {t('cookieBanner.save')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function CategoryRow({ title, desc, checked, disabled, onChange, stateLabel }) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? 'opacity-70' : 'cursor-pointer'}`}>
      <input
        type='checkbox'
        className='mt-1 h-4 w-4 accent-[#FF5A1F]'
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <span className='min-w-0'>
        <span className='text-[0.88rem] font-medium'>
          {title}
          {stateLabel && <span className='ml-2 text-[0.72rem] text-ink/50'>({stateLabel})</span>}
        </span>
        <span className='block text-[0.82rem] leading-[1.5] text-ink/60'>{desc}</span>
      </span>
    </label>
  )
}
